import React from 'react';
import { API } from '../api';
import { applyClubTheme } from '../utils/theme';
import ClubNewsCard from './ClubNewsCard';

/*
 * A club's own page — the front door at greensheetbowls.online/<club>.
 *
 * Written for somebody who is not a member and may never be one: who we
 * are, what we do, where the green is, how to join. The point of it is that
 * a club should not need a website of its own, and that a stranger who
 * searches for their local bowls club should meet a club rather than a
 * password box.
 *
 * Everything on it is written by the club. Nothing is computed from
 * competitions, results or members, so there is no member data here to
 * leak — which is why the route behind it answers without a login at all.
 *
 * Rendered in three places, and it has to behave in all of them:
 *   - the public page, from index.js, for a single-segment address
 *   - a preview inside the admin panel, before publishing
 *   - later, a tab inside the app for signed-in members
 * so it takes its slug as a prop and never reads the URL itself.
 *
 * See CLUB_SITE_PLAN.md.
 */

/**
 * A picture in one of the page's fixed frames.
 *
 * `fit` is what the club chose for this particular picture: fill the frame
 * and crop (the default, right for a photograph), or show all of it inside
 * the frame (right for a logo, which must not lose its edges). The position
 * says which part survives the crop, and arrives ready for CSS.
 */
function Photo({ id, alt, className, fit }) {
  const f = fit || {};
  /* /club-photo/:id.jpg serves without a login, but only for pictures
     flagged as shared — which saving the page does for everything on it. */
  return (
    <img
      className={`${className || ''}${f.fit === 'contain' ? ' clubsite-photo-whole' : ''}`}
      style={f.position ? { objectPosition: f.position } : undefined}
      src={`${API}/club-photo/${id}.jpg`} alt={alt || ''} loading="lazy" />
  );
}

/**
 * Whether the masthead is too narrow to hold the buttons as well as the name.
 *
 * In JavaScript rather than by rendering both and hiding one with CSS: two
 * copies of the same button is two things for a screen reader to announce
 * and two for a test to trip over. Where matchMedia does not exist — jsdom —
 * this answers "not narrow", which is the layout with nothing hidden.
 */
function useNarrow(px) {
  const query = `(max-width: ${px}px)`;
  const [narrow, setNarrow] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const m = window.matchMedia(query);
    const onChange = e => setNarrow(e.matches);
    setNarrow(m.matches);
    if (m.addEventListener) {
      m.addEventListener('change', onChange);
      return () => m.removeEventListener('change', onChange);
    }
    m.addListener(onChange); // older Safari
    return () => m.removeListener(onChange);
  }, [query]);
  return narrow;
}

/* Generous. The club's name, two buttons and a menu button will technically
   fit at around 620px, but only for a short club name and with nothing to
   spare — and "technically fits" is what kept putting the buttons over the
   top of "Oratia Bowling Club". */
const BAR_TOO_NARROW = 820;

/* appOrigin: '' on GreenSheet's own address. On a club's own domain it is the
   main address, so signing in and the membership form go there — one login
   that works everywhere, rather than one per club domain. */
function ClubSite({ slug, embedded = false, onLogin, appOrigin = '' }) {
  const [page, setPage] = React.useState(null);
  const [state, setState] = React.useState('loading'); // loading | ready | missing | error
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef(null);
  const narrow = useNarrow(BAR_TOO_NARROW);

  /* A menu that will not shut is worse than one that will not open, and the
     two ways people expect to shut one are Escape and clicking off it.
     Bound only while it is open, so a closed menu costs nothing. */
  React.useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = e => { if (e.key === 'Escape') setMenuOpen(false); };
    const onDown = e => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [menuOpen]);

  React.useEffect(() => {
    let cancelled = false;
    setState('loading');
    /* No headers: importing api.js installs the wrapper that adds the Bearer
       token to every /api/ call, which is how an admin gets the preview of
       an unpublished page while a stranger gets a 404. */
    fetch(`${API}/api/site/${encodeURIComponent(slug)}`)
      .then(async r => {
        if (r.status === 404) { if (!cancelled) setState('missing'); return null; }
        if (!r.ok) throw new Error('could not load');
        return r.json();
      })
      .then(d => {
        if (cancelled || !d) return;
        setPage(d);
        setState('ready');
      })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; };
  }, [slug]);

  /* The club's colours, so the page looks like the club rather than like
     GreenSheet. Skipped when embedded: the admin panel around the preview
     is already wearing this club's theme, and re-applying it to the document
     from inside a preview would be a surprising thing for a preview to do. */
  React.useEffect(() => {
    if (embedded || !page) return;
    applyClubTheme(page.club.themeC60, page.club.themeC30, page.club.themeC10);
  }, [embedded, page]);

  if (state === 'loading') {
    return <div className="clubsite-centre">Loading…</div>;
  }
  if (state === 'missing') {
    return (
      <div className="clubsite-centre">
        <h1>Club not found</h1>
        <p>There is no club at this address.</p>
        <a className="w3-button w3-round clubsite-btn" href={`${appOrigin}/`}>Go to GreenSheet</a>
      </div>
    );
  }
  if (state === 'error' || !page) {
    return <div className="clubsite-centre">That did not load. Try again in a moment.</div>;
  }

  const { club, sections, contact, links, headings } = page;
  const on = k => !!sections[k];

  /* Every word of every action comes from the server, label included. This
     component deliberately has no fallback copy of its own: a page that can
     say something the club never wrote is the thing this is avoiding. */
  const at = place => (page.actions || []).filter(a => a.place === place);
  const href = kind => (kind === 'join'
    ? `${appOrigin}/membership/${encodeURIComponent(club.slug)}`
    : kind === 'renew' ? `${appOrigin}/membership/${encodeURIComponent(club.slug)}/renew` : null);

  const Action = ({ action, primary }) => {
    if (action.kind === 'news') return <ClubNewsCard pageClubId={club.id} />;
    const cls = `clubsite-btn${primary ? ' clubsite-btn-primary' : ''}`;
    const to = href(action.kind);
    if (to) return <a className={cls} href={to}>{action.label}</a>;
    return (
      <button type="button" className={cls}
        onClick={() => (onLogin ? onLogin() : window.location.assign(`${appOrigin}/`))}>
        {action.label}
      </button>
    );
  };
  const gallery = (page.photoIds || []).filter(id => String(id) !== String(page.heroPhotoId));
  const hasContact = contact.addressLines.length || contact.phone || contact.email || contact.hours;

  /* How the club asked for this particular picture to sit in its frame.
     Undefined for most of them, which is the default: fill and crop. */
  const fitFor = id => (page.photoFit || {})[String(id)];

  /* What the menu can jump to.
   *
     Built from the page rather than from a list of section names, so it can
     never offer somewhere that is not there — a club with no photographs
     gets no "Photos" entry, and turning a section off removes its line
     without anybody maintaining a second list.
   *
     The labels are the club's own headings, for the same reason nothing else
     here has words of its own. That has a consequence worth knowing: a
     section the club left unheaded has nothing to call it, so it does not
     appear in the menu. Give it a heading and it does. */
  const menu = [
    ...(on('welcome')
      ? (page.stories || [])
        .map((s, i) => ({ id: `piece-${i}`, label: s.heading }))
        .filter(m => m.label)
      : []),
    ...(on('photos') && gallery.length && headings.photos
      ? [{ id: 'photos', label: headings.photos }] : []),
    ...(on('contact') && hasContact && headings.contact
      ? [{ id: 'contact', label: headings.contact }] : []),
    ...(on('links') && links.length && headings.links
      ? [{ id: 'links', label: headings.links }] : []),
  ];

  /* Wide enough, and the buttons sit in the masthead where they are easiest
     to find. Too narrow, and they move into the menu — not as buttons, which
     is how this was tried before and came out as two slabs taking a third of
     a phone screen, but as rows exactly like the ones that jump to a part of
     the page. One list, one look, nothing shouting. */
  const barActions = narrow ? [] : at('bar');
  const menuActions = narrow ? at('bar') : [];
  /* The news sign-up behaves differently from the rest — it is a card that
     expands rather than a button that goes somewhere — so it is drawn apart
     from them wherever it lands. */
  const bodyButtons = at('body').filter(a => a.kind !== 'news');
  const bodyNews = at('body').find(a => a.kind === 'news') || null;

  return (
    <div className={`clubsite${embedded ? ' clubsite-embedded' : ''}`}>
      {page.preview && (
        <div className="clubsite-preview-bar" role="status">
          Preview — this page is not published, so only your club’s admins can see it.
        </div>
      )}

      {/* A slim bar carrying the club's identity and the members' way in.
          The name lives here rather than over the photograph: said in both
          places it reads as a mistake, and a masthead is what a visitor
          looks for when they want to know whose site this is. */}
      <header className="clubsite-bar">
        <div className="clubsite-bar-inner">
          <div className="clubsite-brand">
            {club.logo && <img className="clubsite-logo" src={club.logo} alt="" />}
            <h1 className="clubsite-name">{club.name}</h1>
          </div>
          <div className="clubsite-bar-actions">
            {barActions.map(a => <Action key={a.kind} action={a} />)}
          </div>

          {(menu.length > 0 || menuActions.length > 0) && (
              <div className="clubsite-menu-wrap" ref={menuRef}>
                <button type="button" className="clubsite-burger"
                  aria-expanded={menuOpen}
                  aria-controls="clubsite-menu"
                  aria-label={menuOpen ? 'Close the menu' : 'Open the menu'}
                  onClick={() => setMenuOpen(v => !v)}>
                  <i className={`fa ${menuOpen ? 'fa-times' : 'fa-bars'}`} aria-hidden="true"></i>
                </button>
                {/* Rendered only when open, rather than kept in the document
                    behind `hidden`. The browser's own `[hidden] {display:none}`
                    is the weakest rule there is, and this element sets
                    `display: flex` in a class — so `hidden` lost, and the menu
                    would not shut. Not being there at all cannot lose. */}
                {menuOpen && (
                <nav id="clubsite-menu" className="clubsite-menu"
                  aria-label="Menu">
                  {/* The masthead buttons, when the bar was too narrow to
                      hold them. Drawn as rows like everything else here —
                      styled as buttons they became two slabs that dwarfed
                      the links under them. One list, one look. */}
                  {menuActions.map(a => (a.kind === 'login' ? (
                    <button key={a.kind} type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        if (onLogin) onLogin(); else window.location.assign(`${appOrigin}/`);
                      }}>
                      {a.label}
                    </button>
                  ) : (
                    <a key={a.kind} href={href(a.kind)} onClick={() => setMenuOpen(false)}>
                      {a.label}
                    </a>
                  )))}
                  {menu.map(m => (
                    /* A plain anchor, so it works with the keyboard, with a
                       middle click, and with scripting off. The scrolling is
                       the browser's; scroll-margin-top keeps the heading
                       clear of the sticky bar above it. */
                    <a key={m.id} href={`#${m.id}`} onClick={() => setMenuOpen(false)}>
                      {m.label}
                    </a>
                  ))}
                </nav>
                )}
              </div>
            )}
        </div>
      </header>

      <div className="clubsite-hero">
        {page.heroPhotoId
          ? <Photo id={page.heroPhotoId} alt="" className="clubsite-hero-img" fit={fitFor(page.heroPhotoId)} />
          : <div className="clubsite-hero-img clubsite-hero-plain" />}
        <div className="clubsite-hero-text">
          {on('welcome') && page.tagline && <p className="clubsite-tagline">{page.tagline}</p>}
          {at('hero').length > 0 && (
            <div className="clubsite-actions">
              {at('hero').map((a, i) => <Action key={a.kind} action={a} primary={i === 0} />)}
            </div>
          )}
        </div>
      </div>

      <div className="clubsite-body">
        {/* "About us" used to be a section of its own here, and is now simply
            the first piece — so it can have a picture beside it and a button
            under it like everything else, which it could not before for no
            better reason than having been built first. */}

        {/* The club's own pieces: a heading, some words, and a picture
            beside them if it picked one. Alternating sides so half a dozen
            of them read as a page rather than as a wall; one on its own is
            unaffected, and without a picture it is simply prose. */}
        {on('welcome') && (page.stories || []).map((s, i) => (
          <section key={i} id={`piece-${i}`}
            className={`clubsite-section clubsite-story${s.photoId ? '' : ' clubsite-story-plain'}${i % 2 ? ' clubsite-story-flip' : ''}`}>
            {s.photoId && (
              <div className="clubsite-story-pic">
                <Photo id={s.photoId} className="clubsite-photo" fit={fitFor(s.photoId)} />
              </div>
            )}
            <div className="clubsite-story-words">
              {s.heading && <h2>{s.heading}</h2>}
              {s.body.split(/\n{2,}/).map((para, j) => (
                <p key={j} className="clubsite-para">
                  {para.split('\n').map((line, k) => (
                    <React.Fragment key={k}>{k ? <br /> : null}{line}</React.Fragment>
                  ))}
                </p>
              ))}
              {/* One button, under the words it belongs to — the piece about
                  membership wants "Join" beneath it, not at the far end of
                  the page. */}
              {s.button && (
                <div className="clubsite-actions clubsite-story-action">
                  {s.button.kind === 'url'
                    ? (
                      <a className="clubsite-btn clubsite-btn-primary" href={s.button.url}
                        target="_blank" rel="noreferrer noopener">
                        {s.button.label}
                      </a>
                    )
                    : <Action action={s.button} primary />}
                </div>
              )}
            </div>
          </section>
        ))}

        {on('photos') && gallery.length > 0 && (
          <section className="clubsite-section" id="photos">
            {headings.photos && <h2>{headings.photos}</h2>}
            {/* One photograph is a picture of the club; six are a gallery.
                They want different sizes, so the count decides. */}
            <div className={`clubsite-photos${gallery.length === 1 ? ' clubsite-photos-one' : ''}`}>
              {gallery.map(id => <Photo key={id} id={id} className="clubsite-photo" fit={fitFor(id)} />)}
            </div>
          </section>
        )}

        {/* There used to be a whole invitation written into this component —
            a heading, a paragraph and the wording on both buttons, none of
            it the club's. A club writes its own invitation as one of its
            pieces above; what is left here is only what it asked to appear. */}
        {at('body').length > 0 && (
          <section className="clubsite-section clubsite-doing">
            {/* The news sign-up is a card that opens, not a button, and it
                was being squeezed into the same flex row as one — which is
                why it came out as a dark green slab wedged beside a button
                of a different height. Buttons on their own line, the card on
                its own underneath. */}
            {bodyButtons.length > 0 && (
              <div className="clubsite-actions">
                {bodyButtons.map((a, i) => <Action key={a.kind} action={a} primary={i === 0} />)}
              </div>
            )}
            {bodyNews && (
              <div className="clubsite-newscard">
                <Action action={bodyNews} />
              </div>
            )}
          </section>
        )}

        {on('contact') && hasContact && (
          <section className="clubsite-section" id="contact">
            {headings.contact && <h2>{headings.contact}</h2>}
            <div className="clubsite-contact">
              {contact.addressLines.length > 0 && (
                <div>
                  <div className="clubsite-label">{headings.where}</div>
                  {contact.addressLines.map((l, i) => <div key={i}>{l}</div>)}
                  {contact.mapUrl && (
                    <a className="clubsite-link" href={contact.mapUrl} target="_blank" rel="noreferrer">
                      <i className="fa fa-map-marker" aria-hidden="true"></i> {headings.maps}
                    </a>
                  )}
                </div>
              )}
              {contact.hours && (
                <div>
                  <div className="clubsite-label">{headings.when}</div>
                  {contact.hours.split('\n').map((l, i) => <div key={i}>{l}</div>)}
                </div>
              )}
              {(contact.phone || contact.email) && (
                <div>
                  <div className="clubsite-label">{headings.contactHow}</div>
                  {contact.phone && (
                    <div><a className="clubsite-link" href={`tel:${contact.phone.replace(/\s+/g, '')}`}>{contact.phone}</a></div>
                  )}
                  {contact.email && (
                    <div><a className="clubsite-link" href={`mailto:${contact.email}`}>{contact.email}</a></div>
                  )}
                </div>
              )}
            </div>
          </section>
        )}

        {on('links') && links.length > 0 && (
          <section className="clubsite-section" id="links">
            {headings.links && <h2>{headings.links}</h2>}
            <div className="clubsite-links">
              {links.map((l, i) => (
                <a key={i} className="clubsite-linkchip" href={l.url} target="_blank" rel="noreferrer noopener">
                  <i className={`fa ${l.icon}`} aria-hidden="true"></i> {l.label}
                </a>
              ))}
            </div>
          </section>
        )}

      </div>

      <footer className="clubsite-foot">
        {at('foot').length > 0 && (
          <div className="clubsite-actions clubsite-foot-actions">
            {at('foot').map(a => <Action key={a.kind} action={a} />)}
          </div>
        )}
        <span className="clubsite-foot-note">{club.name}</span>
        <span className="clubsite-foot-note clubsite-foot-quiet">powered by GreenSheet</span>
      </footer>
    </div>
  );
}

export default ClubSite;
